import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Alert,
  Select,
  Option,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  IconButton,
  Table,
  Divider,
  Stack,
  Chip,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Trash as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  PaperPlaneTilt as SendIcon,
  FloppyDisk as SaveIcon,
  Lightning as LightningIcon,
  MagnifyingGlass as SearchIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import {
  priceListApi,
  ordersApi,
  productMappingsApi,
  connectionApi,
  integrationApi,
  type Distributor,
  type PriceListItem,
  type OrderItem,
  type Order,
  type CatalogProduct,
  type ApiSubmitPayload,
  type OrderDraftPayload,
} from '../../lib/distributorsApi';
import { apiClient } from '../../lib/api';
import type { Job } from '../../types';
import { usePermission } from '../../hooks/usePermission';
import { useAbcQuote } from '../../hooks/useAbcQuote';
import ApiOrderCart, { cartTotals, type ApiCartLine } from './ApiOrderCart';
import CatalogBrowserModal from './CatalogBrowserModal';
import CatalogItemAddModal from './CatalogItemAddModal';
import OrderDeliveryDetails, { type OrderDetailsState } from './OrderDeliveryDetails';
import SubmitOrderConfirmDialog from './SubmitOrderConfirmDialog';
import { ABC_MAX_ORDER_LINES, APPOINTMENT_TYPES, PO_NUMBER_MAX, deliveryServiceLabel } from './abcConstants';
import { SRS_SERVICE_LABELS, SRS_TBD_DATE, srsPickupTimes } from './srsOrdering';
import { applyPrefill, recordAddressFields } from '../../utils/prefill';
import { extractApiError } from '../../utils/apiError';
import { orderErrorMessage } from './orderErrors';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

interface Props {
  initialDraftId?: number | null;
  onComplete?: () => void;
  /** Deep-link (?tab=create&job=N): preselect this job once jobs are loaded. */
  initialJobId?: number | null;
  onInitialJobConsumed?: () => void;
}

/** Delivery address as the Order JSON field stores it. */
interface DeliveryAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
}

/** What `convert_to_invoice` hands over via sessionStorage. */
interface MaterialOrderHandoff {
  invoice_id?: number;
  line_items: Array<{
    material_spec_id: number;
    quantity?: number;
    description?: string;
    calculation_data?: unknown;
  }>;
}

const EMPTY_ADDRESS: DeliveryAddress = { address: '', city: '', state: '', zip: '' };

const isBlankAddress = (a: DeliveryAddress) => !a.address && !a.city && !a.state && !a.zip;

const isSameAddress = (a: DeliveryAddress, b: DeliveryAddress | null) =>
  !!b && a.address === b.address && a.city === b.city && a.state === b.state && a.zip === b.zip;

// unit_of_measure carries a backend choices constraint; anything the vendor
// uses outside this set is preserved in distributor_uom and displayed as EA.
const KNOWN_UOMS = new Set(['EA', 'BD', 'SQ', 'LF', 'SF', 'GAL', 'LB', 'BX', 'CT', 'CS', 'RL', 'PC', 'PK', 'TB']);
const normalizeUom = (uom: string) => (KNOWN_UOMS.has(uom) ? uom : 'EA');

const newLineId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const EMPTY_DETAILS: OrderDetailsState = {
  shipToNumber: '',
  branchNumber: '',
  fulfillment: 'delivery',
  deliveryService: 'COM',
  requestedDate: '',
  appointmentType: 'AT',
  appointmentFrom: '',
  appointmentTo: '',
  appointmentInstructions: '',
  poNumber: '',
  jobId: null,
  deliveryAddress: { address: '', city: '', state: '', zip: '' },
  notes: '',
};

export default function OrderCreate({ onComplete, initialDraftId, initialJobId, onInitialJobConsumed }: Props) {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<number | null>(null);
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [priceListItems, setPriceListItems] = useState<PriceListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [notes, setNotes] = useState('');
  const [recipientEmail, setRecipientEmail] = useState<string | null>(null);
  useEffect(() => setRecipientEmail(null), [selectedDistributor]);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>(EMPTY_ADDRESS);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [preloadedData, setPreloadedData] = useState<MaterialOrderHandoff | null>(null);
  const [mappingMaterials, setMappingMaterials] = useState(false);
  // What we last seeded, so switching jobs can replace an address the form put
  // there while never touching one the user typed.
  const seededAddressRef = useRef<DeliveryAddress | null>(null);
  const seededDateRef = useRef<string>('');
  // Invoice materials are mapped once. The price-list effect below re-runs on
  // every keystroke in the product search, and remapping would wipe the cart.
  const mappedRef = useRef(false);

  // Deliveries go to the job site, so the job is what fills the address in.
  // Asks for a big page because this is a picker: the default 20 would hide
  // most of an active board's jobs behind nothing.
  const { data: jobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', 'order-picker'],
    queryFn: async () => {
      const response = await apiClient.get<Job[] | { count: number; results: Job[] }>(
        '/api/jobs/?page_size=200'
      );
      return Array.isArray(response.data) ? response.data : response.data?.results || [];
    },
  });

  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await apiClient.get<{
        main_office_address_1?: string;
        main_office_city?: string;
        main_office_state?: string;
        main_office_zip?: string;
      }>('/api/organizations/current/');
      return response.data;
    },
  });

  const officeAddress: DeliveryAddress | null = organization?.main_office_address_1
    ? {
        address: organization.main_office_address_1,
        city: organization.main_office_city || '',
        state: organization.main_office_state || '',
        zip: organization.main_office_zip || '',
      }
    : null;

  // ---- API (live-pricing) mode state ----
  const [apiCart, setApiCart] = useState<ApiCartLine[]>([]);
  const [details, setDetails] = useState<OrderDetailsState>(EMPTY_DETAILS);
  const [addModalProduct, setAddModalProduct] = useState<CatalogProduct | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadedDraft, setLoadedDraft] = useState<Order | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftLoadError, setDraftLoadError] = useState('');
  const [draftId, setDraftId] = useState<number | null>(null);

  useEffect(() => {
    if (!initialDraftId) return;
    let current = true;
    setDraftLoading(true); setDraftLoadError('');
    ordersApi.get(initialDraftId).then(order => {
      if (!current) return;
      setLoadedDraft(order);
      if (order.status !== 'draft' || order.can_edit === false) {
        setDraftLoadError('This order is no longer an editable draft. Open it in Order History.'); return;
      }
      setDraftId(order.id); setSelectedDistributor(order.distributor); setSelectedJob(order.job || null);
      setCart(order.items || []); setNotes(order.notes || '');
      setDeliveryAddress(order.delivery_address || EMPTY_ADDRESS); setDeliveryDate(order.requested_delivery_date || '');
      setDetails({ ...EMPTY_DETAILS, jobId: order.job || null, notes: order.notes || '',
        contactPhone: String(order.provider_metadata?.contact_phone || ''),
        contactName: String(order.provider_metadata?.contact_name || ''),
        contactEmail: String(order.provider_metadata?.contact_email || ''),
        deliveryAddress: order.delivery_address || EMPTY_ADDRESS, requestedDate: order.requested_delivery_date || '',
        shipToNumber: order.ship_to_number || '', branchNumber: order.branch_number || '', poNumber: order.po_number || '',
        fulfillment: ['CPU', 'EXP', 'WILLCALL'].includes(order.delivery_service || '') ? 'pickup' : 'delivery',
        deliveryService: order.delivery_service || 'COM',
        appointmentType: order.delivery_appointment?.type_code || 'AT',
        appointmentFrom: order.delivery_appointment?.from_time || '',
        appointmentTo: order.delivery_appointment?.to_time || '',
        appointmentInstructions: order.delivery_appointment?.instructions || '',
      });
      setApiCart((order.items || []).map((item, index) => ({
        lineId: item.external_line_id || `draft-${order.id}-${index}`, item_number: item.product_id || '',
        description: item.product_name, quantity: Number(item.quantity), uom: item.distributor_uom || item.unit_of_measure,
        uomOptions: [{ code: item.distributor_uom || item.unit_of_measure, name: item.unit_of_measure, description: '', is_stocking: true }],
        is_dimensional: !!item.is_dimensional, length_value: item.length_value == null ? undefined : Number(item.length_value),
        length_uom: item.length_uom, notes: item.notes,
        variations: item.length_value ? [{ value: Number(item.length_value), uom: item.length_uom || 'FT', size: String(item.length_value) }] : [],
      })));
    }).catch(() => current && setDraftLoadError('Unable to load this draft. Refresh to retry.'))
      .finally(() => current && setDraftLoading(false));
    return () => { current = false; };
  }, [initialDraftId]);

  const { can } = usePermission();

  const selectedDistributorObj = useMemo(
    () => distributors.find((distributor) => distributor.id === selectedDistributor) || null,
    [distributors, selectedDistributor],
  );
  const deliveryEmail = recipientEmail ?? selectedDistributorObj?.default_order_email ?? '';
  // Any api_* distributor drives the live-pricing UI — the specifics
  // (capabilities, limits, delivery vocabulary) come from /integration/.
  // One gate for the whole live path: with the flag off an API-integrated
  // distributor falls back to the uploaded price list and emailed PO, which is
  // exactly what the backend allows in that state.
  const liveOrdering = useFeatureFlag('abc_supply_integration');
  const isApiDistributor =
    liveOrdering && (selectedDistributorObj?.integration_type || '').startsWith('api_');

  const { data: connection, error: connectionError, refetch: refreshConnection } = useQuery({
    queryKey: ['distributor-connection', selectedDistributor],
    queryFn: () => connectionApi.get(selectedDistributor!),
    enabled: !!selectedDistributor && isApiDistributor,
    gcTime: 0,
  });
  const apiMode = !!(isApiDistributor && connection?.status === 'active');
  const isSrs = selectedDistributorObj?.integration_type === 'api_srs';
  const { data: integration } = useQuery({
    queryKey: ['distributor-integration', selectedDistributor],
    queryFn: () => integrationApi.get(selectedDistributor!),
    enabled: !!selectedDistributor && isApiDistributor,
  });
  const canSubmitOrders = can('distributor_orders', 'create')
    && (selectedDistributorObj?.integration_type !== 'api_abc' || integration?.can_submit_orders === true);
  const maxOrderLines = integration?.limits?.order_max_lines ?? ABC_MAX_ORDER_LINES;
  const poNumberMax = integration?.limits?.po_number_max_length ?? PO_NUMBER_MAX;
  const vendorName = integration?.display_name || selectedDistributorObj?.name || 'Distributor';
  useEffect(() => {
    if (!isSrs) return;
    setDetails(current => ({ ...current,
      deliveryService: ['COM', 'CPU', 'EXP'].includes(current.deliveryService) ? (current.fulfillment === 'pickup' ? 'WILLCALL' : 'GROUND_DROP') : current.deliveryService,
      appointmentType: ['AM', 'PM', 'ANYTIME'].includes(current.appointmentType) ? current.appointmentType : 'ANYTIME',
    }));
  }, [isSrs]);


  const { pricing, quoting, quoteStale, quoteError, connectionLost } = useAbcQuote({
    distributorId: selectedDistributor,
    enabled: apiMode,
    shipToNumber: details.shipToNumber || null,
    branchNumber: details.branchNumber || null,
    lines: apiCart,
    maxQuoteLines: integration?.limits?.pricing_max_lines,
  });

  useEffect(() => {
    loadDistributors();
    if (!initialDraftId) checkForMaterialOrderData();
  }, []);

  const checkForMaterialOrderData = () => {
    // Check if there's material order data from invoice conversion
    const materialOrderData = sessionStorage.getItem('materialOrderData');
    if (materialOrderData) {
      const data = JSON.parse(materialOrderData) as MaterialOrderHandoff;
      setPreloadedData(data);
      sessionStorage.removeItem('materialOrderData');
    }
  };

  /**
   * Fill the blank delivery fields, and remember the result as ours when the
   * address was empty to begin with - that is what lets a later job change
   * replace it while leaving an address the user typed alone.
   */
  const seedDeliveryAddress = (incoming: DeliveryAddress) => {
    const wasBlank = isBlankAddress(deliveryAddress);
    const next = applyPrefill(deliveryAddress, incoming);
    if (next === deliveryAddress) return;
    setDeliveryAddress(next);
    if (wasBlank) seededAddressRef.current = next;
  };

  // No job means shop stock, so deliver to where the shop is.
  useEffect(() => {
    if (selectedJob || !officeAddress) return;
    seedDeliveryAddress(officeAddress);
  }, [organization, selectedJob]);

  // The handoff names the invoice, and the invoice knows its job - that is how
  // a converted estimate's materials reach the right job site.
  useEffect(() => {
    if (!preloadedData?.invoice_id || selectedJob) return;
    apiClient
      .get<{ job?: number | null }>(`/api/billing/invoices/${preloadedData.invoice_id}/`)
      .then((response) => {
        if (response.data?.job) setSelectedJob(response.data.job);
      })
      .catch(() => {
        // The order is still perfectly usable without the job attribution.
      });
  }, [preloadedData]);

  // A job picked for us by the handoff above lands before the job list does, so
  // seed when the list resolves. Gap-filling only - picking a job by hand goes
  // through handleJobChange, which may also replace an address it seeded.
  useEffect(() => {
    const job = jobs.find((j) => j.id === selectedJob);
    const address = recordAddressFields(job);
    if (!address) return;

    seedDeliveryAddress({
      address: address.address_1,
      city: address.city,
      state: address.state,
      zip: address.zip_code,
    });
    if (job?.build_date && !deliveryDate) {
      setDeliveryDate(job.build_date);
      seededDateRef.current = job.build_date;
    }
  }, [selectedJob, jobs]);

  useEffect(() => {
    // API-mode distributors price from the live catalog; there is no local
    // price list to load for them.
    if (!selectedDistributor || isApiDistributor) return;
    loadPriceList().then((items) => {
      // Map against the list we just loaded rather than `priceListItems`, which
      // still holds the previous distributor's products at this point - reading
      // it is why every high-confidence match used to be dropped.
      if (preloadedData && !mappedRef.current) {
        mappedRef.current = true;
        handlePreloadedOrder(items);
      }
    });
  }, [selectedDistributor, searchTerm, isApiDistributor]);

  /**
   * Seed the delivery fields from the job the user just picked.
   *
   * Replaces the address only while it is still ours - blank, or exactly what
   * we last seeded. Once someone has typed a delivery address of their own,
   * changing job fills the gaps but never overwrites what they wrote.
   */
  const handleJobChange = (jobId: number | null) => {
    setSelectedJob(jobId);
    // Mirror into the API path's state (see handleJobSelected).
    setDetails((current) => ({ ...current, jobId }));

    const job = jobs.find((j) => j.id === jobId);
    const address = recordAddressFields(job);
    const seeded: DeliveryAddress | null = address
      ? { address: address.address_1, city: address.city, state: address.state, zip: address.zip_code }
      : officeAddress;
    if (!seeded) return;

    const ours = isBlankAddress(deliveryAddress) || isSameAddress(deliveryAddress, seededAddressRef.current);
    setDeliveryAddress(ours ? seeded : applyPrefill(deliveryAddress, seeded));
    seededAddressRef.current = ours ? seeded : null;

    // Materials should land on site for the build, not weeks before it.
    const buildDate = job?.build_date || '';
    if (buildDate && (!deliveryDate || deliveryDate === seededDateRef.current)) {
      setDeliveryDate(buildDate);
      seededDateRef.current = buildDate;
    }
  };

  const handlePreloadedOrder = async (items: PriceListItem[]) => {
    if (!preloadedData || !selectedDistributor) return;

    setMappingMaterials(true);
    setLoading(true);

    try {
      const mappedProducts = await productMappingsApi.mapMaterials(
        preloadedData.line_items,
        selectedDistributor,
        true
      );

      // Add mapped products to cart
      const newCartItems: OrderItem[] = [];

      for (const mappedProduct of mappedProducts) {
        const quantity = mappedProduct.quantity ?? 0;
        const priceListItem = items.find((item) => item.id === mappedProduct.price_list_item_id);

        if (
          !mappedProduct.requires_manual_selection &&
          priceListItem &&
          mappedProduct.unit_price !== undefined
        ) {
          // Automatically add high-confidence matches
          newCartItems.push({
            price_list_item: priceListItem.id,
            product_name: mappedProduct.product_name || priceListItem.product_name,
            product_id: mappedProduct.product_id,
            unit_price: mappedProduct.unit_price.toString(),
            unit_of_measure: priceListItem.unit_of_measure,
            quantity,
            total_price: (mappedProduct.unit_price * quantity).toFixed(2),
            notes: mappedProduct.notes,
          });
        } else if (mappedProduct.suggestions && mappedProduct.suggestions.length > 0) {
          // For manual selection, add the first suggestion
          const suggestion = mappedProduct.suggestions[0];
          const suggested = items.find((item) => item.id === suggestion.price_list_item_id);
          newCartItems.push({
            price_list_item: suggestion.price_list_item_id,
            product_name: suggestion.product_name,
            product_id: suggestion.product_id,
            unit_price: suggestion.unit_price.toString(),
            unit_of_measure: suggested?.unit_of_measure || 'EA',
            quantity,
            total_price: (suggestion.unit_price * quantity).toFixed(2),
            notes: 'Manual selection required - please verify',
          });
        }
      }

      setCart(newCartItems);
      setSuccess(`Automatically mapped ${newCartItems.length} materials from invoice`);
    } catch (err: any) {
      setError('Failed to map materials. Please select products manually.');
    } finally {
      setLoading(false);
      setMappingMaterials(false);
    }
  };

  // Auto-select best distributor if we have preloaded data
  useEffect(() => {
    if (preloadedData && distributors.length > 0 && !selectedDistributor) {
      suggestBestDistributor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedData, distributors]);

  const suggestBestDistributor = async () => {
    if (!preloadedData) return;

    try {
      const materialSpecIds = preloadedData.line_items
        .map((item) => item.material_spec_id)
        .filter((id): id is number => !!id);

      if (materialSpecIds.length === 0) {
        // No material specs, just select the first distributor
        if (distributors.length > 0) {
          setSelectedDistributor(distributors[0].id);
        }
        return;
      }

      const suggestion = await productMappingsApi.suggestDistributor(materialSpecIds);

      if (suggestion.recommended) {
        setSelectedDistributor(suggestion.recommended.distributor_id);
        setSuccess(`Selected ${suggestion.recommended.distributor_name} - ${suggestion.recommended.coverage_percent.toFixed(0)}% material availability`);
      }
    } catch (err) {
      // Fall back to first distributor
      if (distributors.length > 0) {
        setSelectedDistributor(distributors[0].id);
      }
    }
  };

  const loadDistributors = async () => {
    try {
      const distributorList = await apiClient.getAllPages<Distributor>('/api/distributors/distributors/?is_active=true');
      setDistributors(distributorList.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load distributors');
      setDistributors([]);
    }
  };

  const loadPriceList = async (): Promise<PriceListItem[]> => {
    if (!selectedDistributor) return [];

    try {
      const response = await priceListApi.list({
        distributor: selectedDistributor,
        search: searchTerm || undefined,
        is_active: true,
      });
      const items = response?.results || [];
      setPriceListItems(items);
      return items;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load price list');
      setPriceListItems([]);
      return [];
    }
  };

  // ---- API mode: jobs, defaults, deep-link ----

  // Default ship-to/branch from the connection once it loads.
  useEffect(() => {
    if (!apiMode || !connection || details.shipToNumber || details.branchNumber) return;
    const shipTos = connection.ship_tos || [];
    const defaultShipTo = shipTos.find((shipTo) => shipTo.is_default) || shipTos[0];
    if (!defaultShipTo) return;
    const home = defaultShipTo.branches.find(branch => branch.number === connection.default_branch_number)
      || defaultShipTo.branches.find((branch) => branch.homeBranch) || defaultShipTo.branches[0];
    setDetails((current) => ({
      ...current,
      shipToNumber: defaultShipTo.number,
      branchNumber: home?.number || '',
    }));
  }, [apiMode, connection, details.shipToNumber, details.branchNumber]);

  const handleJobSelected = (jobId: number | null) => {
    const job = jobs.find((entry) => entry.id === jobId) || null;
    // Mirror into the email path's state so the job survives a distributor switch.
    setSelectedJob(jobId);
    setDetails((current) => ({
      ...current,
      jobId,
      // Prefill delivery details from the job; PO defaults to the job number.
      ...(job
        ? {
            deliveryAddress: {
              address: job.address_1 || '',
              city: job.city || '',
              state: job.state || '',
              zip: job.zip_code || '',
            },
            poNumber: current.poNumber || job.job_number.slice(0, poNumberMax),
          }
        : {}),
    }));
  };

  // Consume the ?tab=create&job=N deep link once jobs are available.
  useEffect(() => {
    if (!initialJobId || jobs.length === 0) return;
    handleJobSelected(initialJobId);
    onInitialJobConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJobId, jobs]);

  // ---- API mode: cart management ----

  const handleCatalogAdd = (product: CatalogProduct) => {
    if (apiCart.length >= maxOrderLines) {
      setError(`${vendorName} orders are limited to ${maxOrderLines} lines.`);
      return;
    }
    const needsChoices = product.is_dimensional || product.uoms.length > 1;
    if (needsChoices) {
      setAddModalProduct(product);
      return;
    }
    addApiLine(product, {
      uom: product.uoms[0]?.code || 'EA',
      quantity: 1,
    });
  };

  const addApiLine = (
    product: CatalogProduct,
    choice: { uom: string; quantity: number; length_value?: number; length_uom?: string },
  ) => {
    setApiCart((current) => {
      const existing = current.find(
        (line) =>
          line.item_number === product.item_number
          && line.uom === choice.uom
          && (line.length_value ?? null) === (choice.length_value ?? null)
          && (line.length_uom ?? '') === (choice.length_uom ?? ''),
      );
      if (existing) {
        return current.map((line) =>
          line.lineId === existing.lineId
            ? { ...line, quantity: line.quantity + choice.quantity }
            : line,
        );
      }
      return [
        ...current,
        {
          lineId: newLineId(),
          item_number: product.item_number,
          description: product.description,
          supplier_name: product.supplier_name,
          uomOptions: product.uoms,
          uom: choice.uom,
          quantity: choice.quantity,
          is_dimensional: product.is_dimensional,
          variations: product.dimension_options,
          length_value: choice.length_value,
          length_uom: choice.length_uom,
          image_asset_id: product.image_asset_id,
        },
      ];
    });
    setAddModalProduct(null);
  };

  const updateApiLine = (lineId: string, patch: Partial<ApiCartLine>) => {
    setApiCart((current) =>
      current.map((line) => (line.lineId === lineId ? { ...line, ...patch } : line)),
    );
  };

  const handleApiQuantityChange = (lineId: string, quantity: number) => {
    if (!Number.isSafeInteger(quantity)) return;
    if (quantity <= 0) {
      setApiCart((current) => current.filter((line) => line.lineId !== lineId));
      return;
    }
    updateApiLine(lineId, { quantity });
  };

  const handleApiLengthChange = (lineId: string, variationIndex: number) => {
    const line = apiCart.find((entry) => entry.lineId === lineId);
    const variation = line?.variations[variationIndex];
    if (!variation) return;
    updateApiLine(lineId, { length_value: variation.value, length_uom: variation.uom });
  };

  const totals = cartTotals(apiCart, pricing);
  const appointmentConfig = APPOINTMENT_TYPES.find((type) => type.value === details.appointmentType);
  const appointmentValid =
    details.fulfillment === 'pickup'
    || !appointmentConfig
    || ((!appointmentConfig.requiresFrom || !!details.appointmentFrom)
      && (!appointmentConfig.requiresTo || !!details.appointmentTo));

  const selectedSrsBranch = connection?.ship_tos?.find(account => account.number === details.shipToNumber)?.branches.find(branch => branch.number === details.branchNumber);
  const srsDetailsValid = !isSrs || (
    !!details.contactPhone?.trim() && !!details.requestedDate
    && Object.values(details.deliveryAddress).every(value => !!value.trim())
    && (details.fulfillment === 'pickup'
      ? srsPickupTimes(selectedSrsBranch, details.requestedDate).includes(details.appointmentFrom)
      : !!selectedSrsBranch?.delivery_services?.includes(details.deliveryService))
  );
  const abcDetailsValid = selectedDistributorObj?.integration_type !== 'api_abc' || (
    !!details.contactName?.trim() && !!details.contactPhone?.trim()
    && (details.fulfillment !== 'pickup' || !!details.contactEmail?.trim())
    && (!details.contactEmail?.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.contactEmail.trim()))
    && (details.fulfillment !== 'delivery' || details.appointmentInstructions.length <= 255)
  );
  const apiCanSubmit = abcDetailsValid && srsDetailsValid &&
    apiCart.length > 0
    && apiCart.length <= maxOrderLines
    && totals.errorCount === 0
    && totals.pricedCount === apiCart.length
    && totals.missingLengthCount === 0
    && !!details.shipToNumber
    && !!details.branchNumber
    && !!details.poNumber
    && details.poNumber.length <= poNumberMax
    && appointmentValid
    && !quoting &&
    // Also covers the debounce gap before a re-quote starts.
    !quoteStale &&
    !connectionLost &&
    canSubmitOrders;

  const mapApiCartToItems = (): OrderItem[] =>
    apiCart.map((line) => {
      const price = pricing[line.lineId];
      return {
        product_name: line.description,
        product_id: line.item_number,
        unit_price: price?.state === 'priced' ? price.unit_price.toFixed(2) : '0.00',
        unit_of_measure: normalizeUom(line.uom),
        distributor_uom: line.uom,
        quantity: line.quantity,
        is_dimensional: line.is_dimensional,
        length_value: line.length_value ?? undefined,
        length_uom: line.length_uom || undefined,
        external_line_id: line.lineId,
        notes: line.notes,
      };
    });

  const buildDraftPayload = (): OrderDraftPayload => ({
    contact_phone: details.contactPhone,
    contact_name: details.contactName,
    contact_email: details.contactEmail,
    distributor: selectedDistributor!,
    items: mapApiCartToItems(),
    notes: details.notes,
    delivery_address: (isSrs || details.fulfillment === 'delivery') && details.deliveryAddress.address
      ? details.deliveryAddress
      : undefined,
    requested_delivery_date: details.requestedDate || undefined,
    job: details.jobId,
    po_number: details.poNumber,
    ship_to_number: details.shipToNumber,
    branch_number: details.branchNumber,
    branch_name: connection?.ship_tos
      ?.find((shipTo) => shipTo.number === details.shipToNumber)
      ?.branches.find((branch) => branch.number === details.branchNumber)?.name || '',
    delivery_service: details.deliveryService,
    delivery_appointment: (isSrs || details.fulfillment === 'delivery')
      ? {
          type_code: details.appointmentType,
          from_time: details.appointmentFrom || undefined,
          to_time: details.appointmentTo || undefined,
          instructions: details.appointmentInstructions || undefined,
        }
      : {},
  });

  const buildSubmitPayload = (): ApiSubmitPayload => ({
    contact_phone: details.contactPhone,
    contact_name: details.contactName,
    contact_email: details.contactEmail,
    po_number: details.poNumber,
    ship_to_number: details.shipToNumber,
    branch_number: details.branchNumber,
    delivery_service: details.deliveryService,
    requested_delivery_date: details.requestedDate || null,
    delivery_appointment: (isSrs || details.fulfillment === 'delivery')
      ? {
          type_code: details.appointmentType,
          from_time: details.appointmentFrom || undefined,
          to_time: details.appointmentTo || undefined,
          instructions: details.appointmentInstructions || undefined,
        }
      : null,
  });

  const saveApiDraft = async (): Promise<number> => {
    const payload = buildDraftPayload();
    if (draftId) {
      await ordersApi.update(draftId, payload as any);
      return draftId;
    }
    const order = await ordersApi.create(payload);
    setDraftId(order.id);
    return order.id;
  };

  const resetApiForm = () => {
    setApiCart([]);
    setLoadedDraft(null);
    window.history.replaceState({}, document.title, window.location.pathname);
    setDetails(EMPTY_DETAILS);
    setDraftId(null);
    setSelectedDistributor(null);
  };

  const handleApiSaveDraft = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const id = await saveApiDraft();
      setSuccess(`Draft order saved (#${id}). Find it under Order History.`);
    } catch (err: any) {
      setError(`${orderErrorMessage(err, 'Failed to save draft')} — ${err.status && err.status < 500 ? 'changes were not saved' : 'the draft save could not be confirmed'}. Your entries are still here; check Order History before creating another draft.`);
    } finally {
      setLoading(false);
    }
  };

  const handleApiSubmit = async () => {
    if (!apiCanSubmit || submitting) {
      setConfirmOpen(false);
      setError('Review the order details and wait for current pricing before submitting.');
      return;
    }
    let submissionStarted = false;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const id = await saveApiDraft();
      submissionStarted = true;
      const submitted = await ordersApi.submit(id, buildSubmitPayload());
      setConfirmOpen(false);
      setSuccess(
        `Order submitted to ${selectedDistributorObj?.name} — confirmation #${submitted.confirmation_number || 'pending'}`,
      );
      resetApiForm();
      if (onComplete) {
        setTimeout(onComplete, 2000);
      }
    } catch (err: any) {
      setConfirmOpen(false);
      const data = err.response?.data ?? err.responseData ?? {};
      const message = orderErrorMessage(err, 'Failed to submit order');
      setError(!submissionStarted
        ? `${message} — ${err.status && err.status < 500 ? 'changes were not saved' : 'the draft save could not be confirmed'} and no order was sent to the distributor. Your entries are still here; ${err.status && err.status < 500 ? 'correct the issue and try again' : 'check Order History before creating another draft'}.`
        : data.outcome === 'unknown' || !err.status || err.status >= 500
          ? 'The distributor may have received this order. Open Order History and check with the branch before trying again.'
          : `${message} — your draft is saved; fix the issue and press Submit again, or find it in Order History.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Email mode (original flow, unchanged) ----

  const addToCart = (item: PriceListItem) => {
    const existingItem = cart.find(
      (cartItem) => cartItem.price_list_item === item.id
    );

    if (existingItem) {
      // Increment quantity if already in cart
      updateQuantity(cart.indexOf(existingItem), existingItem.quantity + 1);
    } else {
      // Add new item to cart
      const newItem: OrderItem = {
        price_list_item: item.id,
        product_name: item.product_name,
        product_id: item.product_id,
        unit_price: item.unit_price,
        unit_of_measure: item.unit_of_measure,
        quantity: 1,
        total_price: item.unit_price,
      };
      setCart([...cart, newItem]);
    }
  };

  const updateQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }

    const updatedCart = [...cart];
    updatedCart[index].quantity = quantity;
    updatedCart[index].total_price = (
      parseFloat(updatedCart[index].unit_price) * quantity
    ).toFixed(2);
    setCart(updatedCart);
  };

  const removeFromCart = (index: number) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return cart
      .reduce((sum, item) => sum + parseFloat(item.total_price || '0'), 0)
      .toFixed(2);
  };

  const handleSubmit = async (submitOrder = false) => {
    if (!selectedDistributor || !cart || cart.length === 0) {
      setError('Please select a distributor and add items to cart');
      return;
    }

    if (submitOrder && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryEmail.trim())) {
      setError('Enter a valid distributor email address before submitting.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create order
      const orderData = {
        distributor: selectedDistributor,
        // Attributing the order to a job is what lets its cost land on the job
        // instead of being retyped as a JobCost later.
        job: selectedJob ?? undefined,
        items: cart,
        notes,
        delivery_address: deliveryAddress.address ? deliveryAddress : undefined,
        requested_delivery_date: deliveryDate || undefined,
      };

      const order = draftId
        ? await ordersApi.update(draftId, orderData)
        : await ordersApi.create(orderData);
      setDraftId(order.id);

      if (submitOrder) {
        // Submit the order immediately
        const submitted = await ordersApi.submit(order.id, {
          generate_pdf: true,
          send_email: true,
          recipient_email: deliveryEmail.trim(),
        });
        if (submitted.email_result?.success === false) {
          setError(
            `Order ${order.order_number} was saved, but email failed: ${submitted.email_result.message}`,
          );
          setCart([]);
          return;
        }
        setSuccess(`Order ${order.order_number} submitted to ${deliveryEmail.trim()}.`);
      } else {
        setSuccess(`Draft order ${order.order_number} saved successfully!`);
      }

      // Clear form
      setCart([]);
      setDraftId(null);
      setLoadedDraft(null);
      window.history.replaceState({}, document.title, window.location.pathname);
      setNotes('');
      setDeliveryAddress(EMPTY_ADDRESS);
      seededAddressRef.current = null;
      setDeliveryDate('');
      seededDateRef.current = '';
      setSelectedDistributor(null);
      setSelectedJob(null);

      if (onComplete) {
        setTimeout(onComplete, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const summaryShipTo = connection?.ship_tos?.find((shipTo) => shipTo.number === details.shipToNumber);
  const summaryBranch = summaryShipTo?.branches.find((branch) => branch.number === details.branchNumber);
  // Name the branch the browser's prices belong to: the same Ship-To/branch
  // the cart quotes against, so the two can never be read against each other.
  const browserShipTo = connection?.ship_tos?.find(
    (shipTo: any) => shipTo.number === details.shipToNumber,
  );
  const browserBranchLabel =
    (browserShipTo?.branches || []).find((branch: any) => branch.number === details.branchNumber)
      ?.name || '';

  const summaryJob = jobs.find((job) => job.id === details.jobId);

  // The cart pane carries far more per row (UOM, length, qty, price, total,
  // remove) than the product picker, so it gets ~2/3 of the width from lg up.
  // md stays gentler so the search results don't get squeezed on laptops.
  if (draftLoading) return <Alert>Loading saved materials order…</Alert>;
  if (draftLoadError) return <Alert color="warning">{draftLoadError}</Alert>;
  if (loadedDraft?.takeoff_source && selectedDistributorObj && loadedDraft.takeoff_source.live_catalog !== isApiDistributor) {
    return <Alert color="warning">The distributor ordering mode changed since this review. Reopen the measurement and prepare a new review before ordering.</Alert>;
  }

  if (loadedDraft && (loadedDraft.takeoff_source?.live_catalog || loadedDraft.distributor_details?.integration_type?.startsWith('api_')) && !apiMode) {
    return <Alert color="warning">Your materials draft is saved. Live distributor ordering is unavailable while the connection loads or needs reconnection. An admin can reconnect under Distributors → Edit.</Alert>;
  }

  return (
    <Box data-sentry-block
      sx={{
        display: 'grid',
        // In API mode the catalogue opens full screen, so the left column is
        // just the distributor + browse button and the cart takes the rest.
        // The price-list flow still browses inline and keeps the old split.
        gridTemplateColumns: apiMode
          ? { xs: '1fr', lg: '360px 1fr' }
          : { xs: '1fr', md: '1fr 1.5fr', lg: '1fr 2fr' },
        gap: 3,
        alignItems: 'start',
        // Without this, `1fr` resolves to minmax(auto, 1fr) and the tracks
        // refuse to shrink below their content's min-content width — the cart
        // card then overflows the page instead of honouring the 1:2 ratio.
        '& > *': { minWidth: 0 },
      }}
    >
      {loadedDraft?.takeoff_source && <Alert color="primary" sx={{ gridColumn: '1 / -1' }}>
        <Stack spacing={.5}>
          <Typography level="title-md">{loadedDraft.order_number} · Materials from takeoff revision {loadedDraft.takeoff_source.source_revision}</Typography>
          <Typography level="body-sm">{loadedDraft.takeoff_source.proposal_number ? `Accepted proposal ${loadedDraft.takeoff_source.proposal_number}` : 'No accepted proposal'}{loadedDraft.takeoff_source.opportunity_label ? ` · ${loadedDraft.takeoff_source.opportunity_label}` : ''}. Review delivery and pricing before submitting.</Typography>
          {loadedDraft.takeoff_source.measurement && <Button variant="plain" size="sm" onClick={() => { window.location.href = `/operations/measurements/${loadedDraft.takeoff_source!.measurement}`; }}>View source measurement</Button>}
        </Stack>
      </Alert>}
      {/* Product Selection */}
      <Card>
        <Box sx={{ p: 3 }}>
          <Typography level="h4" sx={{ mb: 3 }}>
            Select Products
          </Typography>

          {/*
            API mode has its own job field in the delivery details panel, which
            also defaults the PO number. Rendering this one too would put two
            job pickers on the same screen holding separate state.
          */}
          {!apiMode && (
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Job (Optional)</FormLabel>
              <Select
                value={selectedJob}
                onChange={(_, value) => handleJobChange(value as number | null)}
                placeholder="Not for a specific job"
              >
                <Option value={null}>Not for a specific job</Option>
                {jobs.map((job) => (
                  <Option key={job.id} value={job.id}>
                    {job.job_number} — {job.address_1}, {job.city}
                  </Option>
                ))}
              </Select>
              <FormHelperText>
                Puts the order's cost on the job and fills in the delivery details.
              </FormHelperText>
            </FormControl>
          )}

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Distributor</FormLabel>
            <Select
              value={selectedDistributor}
              disabled={!!loadedDraft}
              onChange={(_, value) => {
                if (value === selectedDistributor) return;
                setSelectedDistributor(value as number);
                setApiCart([]);
                setDraftId(null);
                const nextIsSrs = distributors.find(distributor => distributor.id === value)?.integration_type === 'api_srs';
                setDetails(current => ({ ...current, shipToNumber: '', branchNumber: '',
                  fulfillment: 'delivery', deliveryService: nextIsSrs ? 'GROUND_DROP' : 'COM',
                  appointmentType: nextIsSrs ? 'ANYTIME' : EMPTY_DETAILS.appointmentType,
                  appointmentFrom: '', appointmentTo: '',
                }));
              }}
              placeholder="Choose a distributor"
            >
              {distributors && distributors.map((dist) => (
                <Option key={dist.id} value={dist.id}>
                  {dist.name}
                  {(dist.integration_type || '').startsWith('api_') ? '  ⚡' : ''}
                </Option>
              ))}
            </Select>
            {isApiDistributor && (
              <Chip size="sm" variant="soft" color="primary" startDecorator={<LightningIcon />} sx={{ mt: 1 }}>
                {integration?.mock_mode ? 'Demo pricing & simulated ordering' : integration?.orders_simulated ? 'Sandbox pricing & simulated ordering' : 'Live pricing & API ordering'}
              </Chip>
            )}
          </FormControl>

          {isApiDistributor && connectionError && <Alert color="danger" sx={{ mb: 2 }}>{extractApiError(connectionError, 'Unable to load distributor accounts.')} Open Distributors → Edit to reconnect or refresh accounts.</Alert>}
          {apiMode && connection?.ship_tos?.length === 0 && <Alert color="warning" sx={{ mb: 2 }}>
            <Stack spacing={1}><Typography level="body-sm">No orderable Ship-To accounts are saved. Pricing and submission require an account and branch. Refresh them here; if none are returned, ask {vendorName} to confirm this login’s accounts in the configured environment.</Typography>
              <Button size="sm" variant="outlined" onClick={async () => {
                try { await connectionApi.refreshAccounts(selectedDistributor!); await refreshConnection(); }
                catch (error) { setError(extractApiError(error, 'Account refresh failed.')); }
              }}>Refresh accounts</Button>
            </Stack>
          </Alert>}
          {isApiDistributor && !apiMode && selectedDistributor && connection && (
            <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
              The {vendorName} account for this distributor is
              {' '}{connection.status === 'disconnected' ? 'not connected' : connection.status === 'error' ? 'in need of reconnection' : connection.status}.
              An admin can connect it under Distributors → Edit. Live pricing and API
              ordering are unavailable until then.
            </Alert>
          )}

          {apiMode && selectedDistributor && (
            <Box>
              {integration?.mock_mode && integration?.catalog_source !== 'sandbox' && <Alert color="warning" sx={{ mb: 2 }}>Simulated demo: products, accounts, prices and orders use sample data. No order is sent to ABC.</Alert>}
              {integration?.mock_mode && integration?.catalog_source === 'sandbox' && <Alert color="warning" sx={{ mb: 2 }}>ABC sandbox products and branches. Demo account, prices and orders are simulated; no order is sent to ABC.</Alert>}
              {!integration?.mock_mode && integration?.orders_simulated && <Alert color="warning" sx={{ mb: 2 }}>Sandbox demo: ABC sandbox data and pricing; order submission is simulated. No order is sent to ABC.</Alert>}
              <Button
                size="lg"
                variant="solid"
                fullWidth
                startDecorator={<SearchIcon />}
                onClick={() => setBrowserOpen(true)}
                disabled={isSrs && (!details.branchNumber || !details.shipToNumber)}
              >
                Browse catalog
              </Button>
              <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                {apiCart.length >= maxOrderLines
                  ? `This order is at the ${maxOrderLines}-line limit.`
                  : isSrs && (!details.branchNumber || !details.shipToNumber)
                    ? 'Select a branch and job account below to browse available products.'
                    : 'Search the distributor catalog with live pricing.'}
              </Typography>
            </Box>
          )}

          {selectedDistributor && !isApiDistributor && (
            <>
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {!priceListItems || priceListItems.length === 0 ? (
                  <Alert color="neutral">
                    No products found. Upload a price list for this distributor.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {priceListItems && priceListItems.map((item) => (
                      <Card key={item.id} variant="outlined">
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1.5,
                          }}
                        >
                          <Box>
                            <Typography level="body-md" fontWeight="lg">
                              {item.product_name}
                            </Typography>
                            <Typography level="body-sm">
                              ${item.unit_price}/{item.unit_of_measure}
                            </Typography>
                          </Box>
                          <IconButton
                            size="sm"
                            onClick={() => addToCart(item)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            </>
          )}
        </Box>
      </Card>

      {/* Order Cart */}
      <Card>
        <Box sx={{ p: 3 }}>
          <Typography level="h4" sx={{ mb: 3 }}>
            <ShoppingCartIcon style={{ marginRight: 8 }} />
            Order Cart
          </Typography>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {apiMode ? (
            <>
              {connection && isSrs && <OrderDeliveryDetails isSrs connection={connection} value={details} onChange={patch => setDetails(current => ({ ...current, ...patch }))} onJobSelected={handleJobSelected} jobs={jobs} jobsLoading={jobsLoading} />}
              {connectionLost && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                  The {vendorName} connection stopped responding — an admin may need to
                  reconnect it under Distributors → Edit. Your cart is kept; you can
                  still Save Draft.
                </Alert>
              )}
              {quoteError && !connectionLost && (
                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                  Live pricing failed: {quoteError}
                </Alert>
              )}

              {apiCart.length === 0 ? (
                <Alert color="neutral">
                  Cart is empty — search the live catalog on the left to add items.
                </Alert>
              ) : (
                <>
                  <ApiOrderCart
                    isSrs={isSrs}
                    maxLines={maxOrderLines}
                    lines={apiCart}
                    pricing={pricing}
                    onQuantityChange={handleApiQuantityChange}
                    onUomChange={(lineId, uom) => updateApiLine(lineId, { uom })}
                    onLengthChange={handleApiLengthChange}
                    onRemove={(lineId) => setApiCart((current) => current.filter((line) => line.lineId !== lineId))}
                    quoting={quoting}
                  />

                  <Divider sx={{ my: 2 }} />

                  {connection && !isSrs && (
                    <OrderDeliveryDetails
                      poNumberMax={poNumberMax}
                      connection={connection}
                      value={details}
                      onChange={(patch) => setDetails((current) => ({ ...current, ...patch }))}
                      onJobSelected={handleJobSelected}
                      jobs={jobs}
                      jobsLoading={jobsLoading}
                    />
                  )}

                  {!canSubmitOrders && (
                    <Alert color="warning" variant="soft" sx={{ mt: 2 }}>
                      Your role doesn't include "Submit Distributor Orders" — you can
                      build and save drafts, and an admin (or a permitted teammate)
                      can submit them. Permissions live under Settings → Roles.
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleApiSaveDraft}
                      disabled={loading || submitting || apiCart.length === 0}
                      startDecorator={<SaveIcon />}
                    >
                      Save Draft
                    </Button>
                    <Button
                      onClick={() => setConfirmOpen(true)}
                      disabled={!apiCanSubmit || submitting}
                      startDecorator={<SendIcon />}
                    >
                      {integration?.mock_mode || integration?.orders_simulated ? 'Submit simulated order' : `Submit to ${selectedDistributorObj?.name || 'distributor'}`}
                    </Button>
                  </Box>
                </>
              )}
            </>
          ) : (
            <>
              {mappingMaterials && (
                <Alert color="primary" sx={{ mb: 2 }}>
                  Mapping invoice materials to distributor products...
                </Alert>
              )}

              {preloadedData && !mappingMaterials && cart.length > 0 && (
                <Alert color="warning" sx={{ mb: 2 }}>
                  Materials have been pre-populated from invoice. Please review quantities and products before submitting.
                </Alert>
              )}

              {!cart || cart.length === 0 ? (
                <Alert color="neutral">Cart is empty</Alert>
              ) : (
                <>
                  <Table size="sm" sx={{ mb: 2 }}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart && cart.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <Typography level="body-sm">
                              {item.product_name}
                            </Typography>
                          </td>
                          <td>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                updateQuantity(index, parseInt(e.target.value) || 0)
                              }
                              size="sm"
                              sx={{ width: 70 }}
                            />
                          </td>
                          <td>${item.unit_price}</td>
                          <td>${item.total_price}</td>
                          <td>
                            <IconButton
                              size="sm"
                              color="danger"
                              onClick={() => removeFromCart(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}>
                          <Typography level="body-md" fontWeight="lg">
                            Total:
                          </Typography>
                        </td>
                        <td colSpan={2}>
                          <Typography level="body-md" fontWeight="lg">
                            ${calculateTotal()}
                          </Typography>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Delivery Date (Optional)</FormLabel>
                      <Input
                        type="date"
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Delivery Address (Optional)</FormLabel>
                      <AddressAutocompleteInput
                        value={deliveryAddress.address}
                        sx={{ mb: 1 }}
                        onChange={(value) => setDeliveryAddress(previous => ({ ...previous, address: value }))}
                        onAddressSelect={(parsed) => setDeliveryAddress(previous => ({ ...previous, address: parsed.address_1, city: parsed.city, state: parsed.state, zip: parsed.zip_code }))}
                      />
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr',
                          gap: 1,
                        }}
                      >
                        <Input
                          placeholder="City"
                          value={deliveryAddress.city}
                          onChange={(e) =>
                            setDeliveryAddress({
                              ...deliveryAddress,
                              city: e.target.value,
                            })
                          }
                        />
                        <Input
                          placeholder="State"
                          value={deliveryAddress.state}
                          onChange={(e) =>
                            setDeliveryAddress({
                              ...deliveryAddress,
                              state: e.target.value,
                            })
                          }
                        />
                        <Input
                          placeholder="ZIP"
                          value={deliveryAddress.zip}
                          onChange={(e) =>
                            setDeliveryAddress({
                              ...deliveryAddress,
                              zip: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Order Notes (Optional)</FormLabel>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        minRows={2}
                      />
                    </FormControl>

                    <FormControl required>
                      <FormLabel>Send purchase order to</FormLabel>
                      <Input
                        type="email"
                        value={deliveryEmail}
                        disabled={loading}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                      />
                      <FormHelperText>
                        Used for this order only. The distributor's saved email stays unchanged.
                      </FormHelperText>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleSubmit(false)}
                        disabled={loading || !cart || cart.length === 0}
                        startDecorator={<SaveIcon />}
                      >
                        Save Draft
                      </Button>
                      <Button
                        onClick={() => handleSubmit(true)}
                        loading={loading}
                        disabled={!cart || cart.length === 0}
                        startDecorator={<SendIcon />}
                      >
                        Submit Order
                      </Button>
                    </Box>
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>
      </Card>

      {/* API-mode dialogs */}
      {apiMode && selectedDistributor && (
        <CatalogBrowserModal
          open={browserOpen}
          onClose={() => setBrowserOpen(false)}
          distributorId={selectedDistributor}
          distributorName={selectedDistributorObj?.name || 'Distributor'}
          onAdd={handleCatalogAdd}
          environmentLabel={integration?.catalog_source === 'sandbox' ? 'ABC sandbox products · simulated prices' : integration?.mock_mode ? 'Demo mode · simulated prices' : integration?.api_environment === 'sandbox' ? 'ABC sandbox' : undefined}
          cartLines={apiCart}
          onQuantityChange={handleApiQuantityChange}
          onRemove={lineId => setApiCart(current => current.filter(line => line.lineId !== lineId))}
          isSrs={isSrs}
          maxLines={maxOrderLines}
          shipToNumber={details.shipToNumber}
          branchNumber={details.branchNumber}
          branchLabel={browserBranchLabel}
        />
      )}

      <CatalogItemAddModal
        open={!!addModalProduct}
        product={addModalProduct}
        onClose={() => setAddModalProduct(null)}
        onConfirm={(choice) => addModalProduct && addApiLine(addModalProduct, choice)}
      />
      <SubmitOrderConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApiSubmit}
        submitting={submitting}
        summary={{
          isSrs,
          mockMode: integration?.mock_mode || integration?.orders_simulated,
          distributorName: selectedDistributorObj?.name || 'distributor',
          lineCount: apiCart.length,
          pricedSubtotal: totals.pricedSubtotal,
          unpricedZeroCount: totals.zeroCount,
          shipToLabel: summaryShipTo
            ? `${summaryShipTo.number} — ${summaryShipTo.name || 'Unnamed'}`
            : details.shipToNumber,
          branchLabel: summaryBranch
            ? `${summaryBranch.name || `Branch ${summaryBranch.number}`}`
            : details.branchNumber,
          deliveryLabel: details.fulfillment === 'pickup'
            ? 'Pickup at branch'
            : isSrs ? SRS_SERVICE_LABELS[details.deliveryService] || details.deliveryService : deliveryServiceLabel(details.deliveryService),
          requestedDate: details.requestedDate === SRS_TBD_DATE ? 'TBD' : details.requestedDate || undefined,
          poNumber: details.poNumber,
          jobLabel: summaryJob?.job_number,
        }}
      />
    </Box>
  );
}
